import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit2, Trash2, X, Save, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';

const Materias = () => {
  const [materias, setMaterias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMateria, setEditingMateria] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nome: ''
  });

  // Carregar matérias do Supabase
  const fetchMaterias = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('materias')
        .select('*')
        .order('nome');

      if (error) {
        console.error('Erro ao carregar matérias:', error);
        alert('Erro ao carregar matérias: ' + error.message);
        return;
      }

      setMaterias(data || []);
    } catch (error) {
      console.error('Erro ao carregar matérias:', error);
      alert('Erro ao carregar matérias');
    } finally {
      setLoading(false);
    }
  };

  // Carregar matérias ao montar o componente
  useEffect(() => {
    fetchMaterias();
  }, []);

  // Criar nova matéria
  const createMateria = async (materiaData) => {
    try {
      const { data, error } = await supabase
        .from('materias')
        .insert([materiaData])
        .select();

      if (error) {
        console.error('Erro ao criar matéria:', error);
        alert('Erro ao criar matéria: ' + error.message);
        return false;
      }

      setMaterias([...materias, data[0]]);
      return true;
    } catch (error) {
      console.error('Erro ao criar matéria:', error);
      alert('Erro ao criar matéria');
      return false;
    }
  };

  // Atualizar matéria existente
  const updateMateria = async (id, materiaData) => {
    try {
      const { data, error } = await supabase
        .from('materias')
        .update(materiaData)
        .eq('id', id)
        .select();

      if (error) {
        console.error('Erro ao atualizar matéria:', error);
        alert('Erro ao atualizar matéria: ' + error.message);
        return false;
      }

      setMaterias(materias.map(materia => 
        materia.id === id ? data[0] : materia
      ));
      return true;
    } catch (error) {
      console.error('Erro ao atualizar matéria:', error);
      alert('Erro ao atualizar matéria');
      return false;
    }
  };

  // Excluir matéria
  const deleteMateria = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta matéria?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('materias')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao excluir matéria:', error);
        alert('Erro ao excluir matéria: ' + error.message);
        return;
      }

      setMaterias(materias.filter(materia => materia.id !== id));
    } catch (error) {
      console.error('Erro ao excluir matéria:', error);
      alert('Erro ao excluir matéria');
    }
  };

  // Função para resetar o formulário
  const resetForm = () => {
    setFormData({ nome: '' });
    setEditingMateria(null);
  };

  // Abrir modal para nova matéria
  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Abrir modal para editar matéria
  const openEditModal = (materia) => {
    setEditingMateria(materia);
    setFormData({
      nome: materia.nome || ''
    });
    setShowModal(true);
  };

  // Fechar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingMateria(null);
  };

  // Salvar matéria (criar ou atualizar)
  const handleSave = async (e) => {
    e.preventDefault();
    
    // Validações básicas
    if (!formData.nome.trim()) {
      alert('Por favor, preencha o nome da matéria');
      return;
    }

    const success = editingMateria 
      ? await updateMateria(editingMateria.id, formData)
      : await createMateria(formData);

    if (success) {
      closeModal();
    }
  };

  // Atualizar campo do formulário
  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Filtrar matérias por busca
  const filteredMaterias = materias.filter(materia => 
    materia.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="materias">
        <div className="loading-state">
          <p>Carregando matérias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="materias">
      <div className="page-header">
        <h1 className="page-title">Matérias</h1>
        <p className="page-subtitle">Gerenciamento de disciplinas do currículo</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon">
              <BookOpen size={24} />
            </div>
          </div>
          <div className="stat-value">{materias.length}</div>
          <div className="stat-label">Total de Matérias</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="content-card">
        <div className="filters-section">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar matérias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-buttons">
            <button className="btn btn-primary" onClick={openCreateModal}>
              <Plus size={16} />
              Nova Matéria
            </button>
          </div>
        </div>

        {/* Materias Table */}
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Matéria</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredMaterias.map(materia => (
                <tr key={materia.id}>
                  <td>
                    <div className="materia-info">
                      <div className="materia-name">{materia.nome}</div>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Editar" onClick={() => openEditModal(materia)}>
                        <Edit2 size={16} />
                      </button>
                      <button className="btn-icon danger" title="Excluir" onClick={() => deleteMateria(materia.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMaterias.length === 0 && (
          <div className="empty-state">
            <p>Nenhuma matéria encontrada.</p>
          </div>
        )}
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingMateria ? 'Editar Matéria' : 'Nova Matéria'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="modal-form">
              <div className="form-group">
                <label>Nome da Matéria *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => updateFormField('nome', e.target.value)}
                  required
                  placeholder="Ex: Matemática, Português, História..."
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <Save size={16} />
                  {editingMateria ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .materias {
          padding: 24px;
        }

        .page-header {
          margin-bottom: 32px;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 8px 0;
        }

        .page-subtitle {
          font-size: 16px;
          color: #64748b;
          margin: 0;
        }

        .loading-state {
          text-align: center;
          padding: 48px;
          color: #64748b;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #3b82f6;
          color: white;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 14px;
          color: #64748b;
          font-weight: 500;
        }

        .content-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        .filters-section {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 1px solid #e2e8f0;
          gap: 16px;
        }

        .search-container {
          position: relative;
          flex: 1;
          max-width: 400px;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
        }

        .search-input {
          width: 100%;
          padding: 12px 12px 12px 44px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .filter-buttons {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
          border: none;
        }

        .btn-primary {
          background: #3b82f6;
          color: white;
        }

        .btn-primary:hover {
          background: #2563eb;
        }

        .btn-secondary {
          background: #f8fafc;
          color: #64748b;
          border: 1px solid #d1d5db;
        }

        .btn-secondary:hover {
          background: #f1f5f9;
          color: #475569;
        }

        .table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: #f8fafc;
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .data-table td {
          padding: 16px;
          border-bottom: 1px solid #f3f4f6;
        }

        .data-table tr:hover {
          background: #f9fafb;
        }

        .materia-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .materia-name {
          font-weight: 600;
          color: #1e293b;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .btn-icon {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: 1px solid #d1d5db;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-icon:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .btn-icon.danger:hover {
          background: #fef2f2;
          border-color: #fca5a5;
          color: #dc2626;
        }

        .empty-state {
          text-align: center;
          padding: 48px 24px;
          color: #64748b;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 24px 0 24px;
          margin-bottom: 24px;
        }

        .modal-header h2 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .modal-close {
          width: 32px;
          height: 32px;
          border-radius: 6px;
          border: none;
          background: #f3f4f6;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .modal-close:hover {
          background: #e5e7eb;
        }

        .modal-form {
          padding: 0 24px 24px 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }

        .form-group input {
          width: 100%;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border-color 0.2s ease;
          box-sizing: border-box;
        }

        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #e5e7eb;
        }

        @media (max-width: 768px) {
          .materias {
            padding: 16px;
          }

          .filters-section {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }

          .search-container {
            max-width: none;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Materias;
